import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import type { ChangeType, Frequency } from '../../../../types/calendar';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import startCase from 'lodash/startCase';
import { usePlanDetailsContext } from '../../../context/PlanDetailsContext';

const PAGE_LIMIT = 50;

export const PROMO_TYPE_COLORS: Record<string, { color: string; bgColor: string }> = {
    TPR: { color: '#D97706', bgColor: '#FFFBEB' },
    BOGO: { color: '#2563EB', bgColor: '#EFF6FF' },
    Display: { color: '#059669', bgColor: '#ECFDF5' },
    'Feature & Display': { color: '#0891B2', bgColor: '#ECFEFF' },
    Feature: { color: '#DC2626', bgColor: '#FEF2F2' },
    Merchandising: { color: '#D93095', bgColor: '#FCECF5' },
    Coupon: { color: '#7C3AED', bgColor: '#F5F3FF' },
    'Temporary Price Reduction': { color: '#D97706', bgColor: '#FFFBEB' },
    Rebate: { color: '#0D9488', bgColor: '#F0FDFA' },
    Bundle: { color: '#4F46E5', bgColor: '#EEF2FF' },
};

const FALLBACK_PALETTE: { color: string; bgColor: string }[] = [
    { color: '#6366F1', bgColor: '#EEF2FF' },
    { color: '#EC4899', bgColor: '#FDF2F8' },
    { color: '#14B8A6', bgColor: '#F0FDFA' },
    { color: '#F59E0B', bgColor: '#FFFBEB' },
    { color: '#8B5CF6', bgColor: '#F5F3FF' },
    { color: '#EF4444', bgColor: '#FEF2F2' },
    { color: '#10B981', bgColor: '#ECFDF5' },
    { color: '#3B82F6', bgColor: '#EFF6FF' },
];

function hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
}

const DEFAULT_COLORS = { color: '#A1A1AA', bgColor: '#F4F4F5' };

function getPromoTypeColors(promoTypeName: string | null | undefined): { color: string; bgColor: string } {
    if (!promoTypeName) return DEFAULT_COLORS;

    const normalized = promoTypeName.trim();
    const match = PROMO_TYPE_COLORS[normalized];
    if (match) return match;

    const caseInsensitive = Object.entries(PROMO_TYPE_COLORS).find(
        ([key]) => key.toLowerCase() === normalized.toLowerCase(),
    );
    if (caseInsensitive) return caseInsensitive[1];

    return FALLBACK_PALETTE[hashString(normalized) % FALLBACK_PALETTE.length];
}

export const getPromoTypeColor = (promoTypeName: string | null | undefined): string =>
    getPromoTypeColors(promoTypeName).color;

export const getPromoTypeBgColor = (promoTypeName: string | null | undefined): string =>
    getPromoTypeColors(promoTypeName).bgColor;

export type CalendarEventData = {
    event_name: string;
    event_id: string;
    event_status: string | null;
    promo_lifecycle_status: string | null;
    promo_name: string | null;
    promo_id: string | null;
    promo_start_date: string | null;
    promo_end_date: string | null;
    promo_status: string | null;
    new_status: string | null;
    attributes: { promotion_type: string | null; tactic: string | null };
    promotion_mechanic: string | null;
    keep_dates: boolean;
    keep_promo: boolean;
    hover_data: {
        promo_status: string;
        attributes: { promotion_type: string | null; tactic: string | null };
        promo_description: string | null;
        promotion_mechanic: string | null;
        promo_dates: string | null;
        products_count: number;
        products: any[];
        performance: {
            uplift: number | null;
            roi: number | null;
            incremental_vol: number | null;
            incremental_revenue: number | null;
        };
    };
    group_key: string | null;
};

export type PlanDetails = {
    plan_id: string;
    plan_name: string;
    planning_group_name: string;
    dim1_attrs: string[];
    dim2_attrs: string[];
    plan_year: number;
    baseline_vol_version: string;
    plan_version: string;
    created_by: string;
    updated_by: string;
};

type CalendarGroupData = {
    group_key: string | null;
    promo_name: string | null;
    promo_id: string | null;
    event_name: string;
    count: number;
    children: CalendarEventData[];
};

type CalendarDataResponse = {
    periods: { period_start_date: string; period_end_date: string }[];
    calendar_data: CalendarEventData[];
    grouped_data: CalendarGroupData[] | null;
    total_count: number;
    total_count_excluding_deleted: number;
    frequency: Frequency;
    plan_details: PlanDetails | null;
};

type CalendarEvent = {
    startDay: number;
    durationDays: number;
    label: string;
    color: string;
    bgColor: string;
    promoType: string;
};

function parseEventIntoPeriods(
    event: {
        startDate: string | null;
        endDate: string | null;
        label: string | null;
        color: string;
        bgColor: string;
        promoType: string;
    },
    periods: { period_start_date: string; period_end_date: string }[],
): Record<string, CalendarEvent[]> {
    const result: Record<string, CalendarEvent[]> = {};
    periods.forEach((p) => {
        result[p.period_start_date] = [];
    });

    if (!event.startDate || !event.endDate) return result;

    const eventStart = dayjs(event.startDate).startOf('day');
    const eventEnd = dayjs(event.endDate).startOf('day');

    if (!eventStart.isValid() || !eventEnd.isValid()) return result;

    const durationDays = eventEnd.diff(eventStart, 'day');

    for (const period of periods) {
        const colStart = dayjs(period.period_start_date).startOf('day');
        const colEnd = dayjs(period.period_end_date).startOf('day');

        if (!eventStart.isAfter(colEnd) && !eventStart.isBefore(colStart)) {
            result[period.period_start_date].push({
                startDay: eventStart.diff(colStart, 'day'),
                durationDays,
                label: event.label ?? '',
                color: event.color,
                bgColor: event.bgColor,
                promoType: event.promoType,
            });
            break;
        }
    }

    return result;
}

function transformCalendarItem(
    item: CalendarEventData,
    periods: { period_start_date: string; period_end_date: string }[],
) {
    const promotionType = item.attributes?.promotion_type ?? null;
    const periodEvents = parseEventIntoPeriods(
        {
            startDate: item.promo_start_date,
            endDate: item.promo_end_date,
            label: item.promo_name,
            color: getPromoTypeColor(promotionType),
            bgColor: getPromoTypeBgColor(promotionType),
            promoType: promotionType ?? '',
        },
        periods,
    );
    return {
        key: item.promo_id ?? item.event_id,
        promo_name: item.promo_name ?? '',
        event_name: item.event_name,
        promo_id: item.promo_id,
        event_id: item.event_id,
        promo_status: item.promo_status,
        promo_lifecycle_status: item.promo_lifecycle_status as ChangeType | null,
        ...periodEvents,
    };
}

const EMPTY_ROW_KEY = '__empty_row__';

function generateFallbackPeriods(frequency: Frequency): { period_start_date: string; period_end_date: string }[] {
    const now = dayjs();
    const yearEnd = now.endOf('year');
    const periods: { period_start_date: string; period_end_date: string }[] = [];

    switch (frequency) {
        case 'W': {
            let cursor = now.startOf('week');
            while (cursor.isBefore(yearEnd) || cursor.isSame(yearEnd, 'day')) {
                periods.push({
                    period_start_date: cursor.format('YYYY-MM-DD'),
                    period_end_date: cursor.add(6, 'day').format('YYYY-MM-DD'),
                });
                cursor = cursor.add(7, 'day');
            }
            break;
        }
        case 'M': {
            let cursor = now.startOf('month');
            while (cursor.isBefore(yearEnd) || cursor.isSame(yearEnd, 'month')) {
                periods.push({
                    period_start_date: cursor.format('YYYY-MM-DD'),
                    period_end_date: cursor.endOf('month').format('YYYY-MM-DD'),
                });
                cursor = cursor.add(1, 'month');
            }
            break;
        }
        case 'Q': {
            const quarterStartMonth = Math.floor(now.month() / 3) * 3;
            let cursor = now.month(quarterStartMonth).startOf('month');
            while (cursor.isBefore(yearEnd) || cursor.isSame(yearEnd, 'month')) {
                const periodEnd = cursor.add(2, 'month').endOf('month');
                periods.push({
                    period_start_date: cursor.format('YYYY-MM-DD'),
                    period_end_date: periodEnd.format('YYYY-MM-DD'),
                });
                cursor = cursor.add(3, 'month');
            }
            break;
        }
    }

    return periods;
}

const EMPTY_ROW_COUNT = 15;

function createEmptyCalendarRows(periods: { period_start_date: string; period_end_date: string }[]) {
    const emptyPeriods: Record<string, CalendarEvent[]> = {};
    periods.forEach((p) => {
        emptyPeriods[p.period_start_date] = [];
    });

    return Array.from({ length: EMPTY_ROW_COUNT }, (_, i) => ({
        key: `${EMPTY_ROW_KEY}_${i}`,
        promo_name: '',
        event_name: '',
        promo_id: '',
        event_id: '',
        promo_status: '' as string | null,
        ...emptyPeriods,
    }));
}

const fetchCalendarData = async (
    planId: string,
    signal: AbortSignal,
    frequency: Frequency,
    searchKeyword: string,
    groupBy: string,
    selectedColumnKeys: string[],
    filters: Record<string, any>,
    offset: number,
    sortBy: string,
    sortingOrder: 'asc' | 'desc',
): Promise<CalendarDataResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getCalendarData(planId), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
        data: {
            frequency,
            sort_by: sortBy,
            sorting_order: sortingOrder,
            limit: PAGE_LIMIT,
            offset,
            search_string: searchKeyword,
            search_fields: selectedColumnKeys,
            filters,
            group_by: groupBy === 'none' ? '' : groupBy,
        },
    });
    return {
        periods: response.data.periods,
        calendar_data: response.data.calendar_data,
        grouped_data: (response.data.grouped_data ?? []).map((_group: any) => ({
            group_key: _group.group_key,
            event_name: startCase(_group.group_label),
            promo_id: _group.group_key,
            count: _group.count,
            children: _group.items,
        })),
        total_count: response.data.total_count,
        total_count_excluding_deleted: response.data.total_count_excluding_deleted,
        frequency,
        plan_details: response.data.plan_details ?? null,
    };
};

const useCalendarData = (
    planId: string,
    frequency: Frequency,
    searchKeyword: string,
    groupBy: string,
    selectedColumnKeys: string[],
    filters: Record<string, any>,
    sortBy: string,
    sortingOrder: 'asc' | 'desc',
) => {
    const { setCurrentPlanDetails } = usePlanDetailsContext();
    const { data, isPending, isFetchingNextPage, error, hasNextPage, fetchNextPage, isFetching } = useInfiniteQuery({
        queryKey: [
            'calendarData',
            planId,
            frequency,
            searchKeyword,
            groupBy,
            selectedColumnKeys,
            filters,
            sortBy,
            sortingOrder,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchCalendarData(
                planId,
                signal,
                frequency,
                searchKeyword,
                groupBy,
                selectedColumnKeys,
                filters,
                pageParam,
                sortBy,
                sortingOrder,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return nextOffset < lastPage.total_count ? nextOffset : undefined;
        },
    });

    const merged = useMemo(() => {
        const buildFallback = () => {
            const fallbackPeriods = generateFallbackPeriods(frequency);
            return { dataSource: createEmptyCalendarRows(fallbackPeriods), periods: fallbackPeriods };
        };

        if (!data?.pages?.length) return buildFallback();

        const firstPage = data.pages[0];
        if (!firstPage) return buildFallback();

        const periods = firstPage.periods;
        const hasGroupedData = (firstPage.grouped_data?.length ?? 0) > 0;

        if (hasGroupedData) {
            const groupMap = new Map<string, CalendarGroupData>();
            for (const page of data.pages) {
                for (const group of page.grouped_data ?? []) {
                    const mapKey = group.group_key ?? '__ungrouped__';
                    const existing = groupMap.get(mapKey);
                    if (existing) {
                        existing.children = [...existing.children, ...group.children];
                        existing.count += group.count;
                    } else {
                        groupMap.set(mapKey, { ...group });
                    }
                }
            }

            const dataSource = Array.from(groupMap.values()).map((group) => {
                const emptyPeriods: Record<string, CalendarEvent[]> = {};
                periods?.forEach((p: { period_start_date: string }) => {
                    emptyPeriods[p.period_start_date] = [];
                });
                return {
                    key: group.group_key ?? group.event_name,
                    promo_name: group.promo_name ?? '',
                    event_name: group.event_name,
                    promo_id: group.promo_id ?? null,
                    event_id: group.group_key ?? group.event_name,
                    promo_status: null as string | null,
                    is_group: true,
                    ...emptyPeriods,
                    children: group.children.map((child: CalendarEventData) => transformCalendarItem(child, periods)),
                };
            });
            return { dataSource, periods };
        }

        const dataSource = data.pages.flatMap((page) =>
            (page.calendar_data ?? []).map((item: CalendarEventData) => transformCalendarItem(item, periods)),
        );

        if (!dataSource.length) {
            const effectivePeriods = periods?.length ? periods : generateFallbackPeriods(frequency);
            return { dataSource: createEmptyCalendarRows(effectivePeriods), periods: effectivePeriods };
        }

        return { dataSource, periods };
    }, [data, frequency]);

    const planDetailsFromData = data?.pages?.[0]?.plan_details ?? null;
    useEffect(() => {
        setCurrentPlanDetails(planDetailsFromData);
    }, [planDetailsFromData, setCurrentPlanDetails]);

    const totalCount = data?.pages?.[0]?.total_count ?? 0;
    const loadedCount = (merged.dataSource as any[]).reduce((acc: number, row: any) => {
        if (typeof row.key === 'string' && row.key.startsWith(EMPTY_ROW_KEY)) return acc;
        return acc + (row.children?.length ?? 1);
    }, 0);

    return {
        dataSource: merged.dataSource,
        periods: merged.periods,
        isPending,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
        totalCount,
        loadedCount,
        error,
    };
};

export default useCalendarData;
